
<template>

    <div class="container" style="height: 100vh; margin-bottom: 700px;">

        <template v-if="hasError">
            <Message severity="error" :closable="false">{{ hasError }}</Message>
        </template>

        <template v-else>

            <Skeleton class="mb-4" width="200px" height="50px" v-if="is_loading" />
            <h2 class="mb-4" v-else>{{ campaign.campaign_key }} ({{ user.user_id ?? '...' }})</h2>

            <div class="card mb-4 shadow-sm">
                <div class="card-body">
                    <ProgressSpinner v-if="is_loading" />
                    <div class="d-flex justify-content-between align-items-start" v-else>
                        <div>
                            <div class="hstack gap-2">
                                <div v-if="campaign?.is_started">
                                    <div class="hstack gap-1" v-if="campaign?.is_active">🟢活動進行中</div>
                                    <div v-else>🔴活動已結束</div>
                                </div>
                                <div v-else-if="campaign?.is_ended">🔴活動已結束</div>
                                <div v-else>⚫活動尚未開始</div>
                                <div>({{ campaign?.for_test?.sd }} ~ {{ campaign?.for_test?.ed }})</div>
                            </div>
                            <div class="hstack gap-2">
                                <div v-if="game?.is_active">🟢購買可以獲得抽紅包</div>
                                <div v-else>🔴購買無法獲得抽紅包</div>
                                <div>({{ game?.start_at }} ~ {{ game?.end_at }})</div>
                            </div>
                        </div>
                        <Button severity="primary" label="重新讀取" @click="fetch" />
                    </div>
                </div>
            </div>

            <Tabs value="list-1">
                <TabList>
                    <Tab value="list-1">活動期間訂購記錄</Tab>
                    <Tab value="list-2">抽獎機會</Tab>
                    <Tab value="list-3">優惠券記錄</Tab>
                    <Tab value="list-4">贈品記錄(所有會員)</Tab>
                </TabList>
                <TabPanels>
                    <TabPanel value="list-1">
                        <ProgressSpinner v-if="is_loading" />
                        <div class="table-responsive" v-else>
                            <table class="table">
                                <thead>
                                    <tr>
                                        <th>訂單編號</th>
                                        <th>狀態</th>
                                        <th>訂購時間</th>
                                        <th>明細</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody v-if="purchases.length > 0">
                                    <tr v-for="purchase in purchases" :key="purchase.id">
                                        <td>{{ purchase.id }}</td>
                                        <td>{{ getPurchaseStatus(purchase.status) }}</td>
                                        <td>{{ getDate(purchase.buy_at) }}</td>
                                        <td>
                                            <ul class="list-unstyled">
                                                <li v-for="detail in purchase?.inventories" :key="detail.id">
                                                    {{ detail.product_name }} x {{ detail.qty }}
                                                </li>
                                            </ul>
                                        </td>
                                        <td class="text-end">
                                            <div class="d-flex justify-content-end align-items-start gap-2">
                                                <Button size="small" severity="secondary" @click="setPurchase($event, purchase, 4)">設為已付款</Button>
                                                <Button size="small" severity="secondary" @click="setPurchase($event, purchase, 0)">設為未付款</Button>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                                <tbody v-else>
                                    <tr>
                                        <td colspan="5" class="text-center">
                                            <Message severity="warn" :closable="false">沒有訂購記錄</Message>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div class="text-muted">
                            <div>福箱每購買一單位會轉換成一次抽獎機會。</div>
                            <div>執行「設為已付款」會發送贈品 (背景佇列執行)，設定後若未出現在明細，稍後重新讀取即可)。</div>
                            <div>執行「設為未付款」僅重計抽獎次數，贈品必須手動回收。</div>
                        </div>

                    </TabPanel>

                    <TabPanel value="list-2">
                        <ProgressSpinner v-if="is_loading" />
                        <template v-else>
                            <div class="table-responsive">
                                <table class="table caption-top">
                                    <caption>抽獎次數: {{ getDrawlotCount() }}</caption>
                                    <thead>
                                        <tr>
                                            <th>訂單編號</th>
                                            <th>商品名稱</th>
                                            <th class="text-center">單價</th>
                                            <th class="text-center">抽獎結果</th>
                                            <th>抽獎於</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="drawlot in drawlots" :key="drawlot.order_id">
                                            <td>{{ drawlot.order_id }}</td>
                                            <td>{{ drawlot.product_name }}</td>
                                            <td class="text-center">{{ toNumeric(drawlot.price) }}</td>
                                            <td class="text-center">
                                                <template v-if="drawlot.drawed">
                                                    <div class="text-danger">{{ toNumeric(drawlot.prize) }}</div>
                                                </template>
                                            </td>
                                            <td>
                                                <div>{{ getDate(drawlot.drawed_at ?? null) }}</div>
                                            </td>
                                            <td class="text-end">
                                                <template v-if="drawlot.drawed">
                                                    <Button size="small" severity="secondary" @click="lotteryDrop($event, drawlot)">重置</Button>
                                                </template>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <p>「重置」抽獎機會僅回收抽獎結果方便測試，優惠券必須手動回收。</p>

                        </template>
                    </TabPanel>

                    <TabPanel value="list-3">
                        <ProgressSpinner v-if="is_loading" />
                        <template v-else>
                            <div class="table-responsive">
                                <table class="table caption-top">
                                    <thead>
                                        <tr>
                                            <th>優惠券編號</th>
                                            <th>優惠券名稱</th>
                                            <th>優惠券金額</th>
                                            <th>優惠券使用期限</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody v-if="vouchers.length > 0">
                                        <tr v-for="voucher in vouchers" :key="voucher.id">
                                            <td>{{ voucher.sn }}</td>
                                            <td>{{ voucher.name }}</td>
                                            <td>{{ voucher.amount }}</td>
                                            <td>{{ voucher?.end_at?.datetime }}</td>
                                            <td>
                                                <Button size="small" severity="secondary" @click="voucherDrop($event, voucher)">回收</Button>
                                            </td>
                                        </tr>
                                    </tbody>
                                    <tbody v-else>
                                        <tr>
                                            <td colspan="5" class="text-center">
                                                <Message severity="warn" :closable="false">沒有此活動的優惠券</Message>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </template>
                    </TabPanel>

                    <TabPanel value="list-4">
                        <ProgressSpinner v-if="is_loading" />
                        <template v-else>
                            <Message severity="info" variant="simple">※ 贈品記錄未區分會員帳號</Message>
                            <div class="table-responsive">
                                <table class="table caption-top">
                                    <caption>贈品數量: {{ gifts.length }} / {{ maxGiftsQty }}</caption>
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>訂單編號</th>
                                            <th>贈品名稱</th>
                                            <th class="text-center">贈品數量</th>
                                            <th class="text-center">發送時間</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody v-if="gifts.length > 0">
                                        <tr v-for="(gift, index) in gifts" :key="gift.id">
                                            <td>{{ index + 1 }}</td>
                                            <td>{{ gift.order_id }}</td>
                                            <td>{{ gift.product_name }}</td>
                                            <td class="text-center">{{ gift.qty }}</td>
                                            <td class="text-center">{{ getDate(gift.created_at) }}</td>
                                            <td class="text-end">
                                                <Button size="small" severity="secondary" @click="giftDrop($event, gift)">回收</Button>
                                            </td>
                                        </tr>
                                    </tbody>
                                    <tbody v-else>
                                        <tr>
                                            <td colspan="6" class="text-center">
                                                <Message severity="warn" :closable="false">沒有贈品記錄</Message>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </template>
                    </TabPanel>
                </TabPanels>
            </Tabs>

        </template>
    </div>

</template>

<script setup>
import ApiClient from '../../../composables/api_client';
import { ref, onMounted } from 'vue'
import { toNumeric } from '../../../composables/utils';
import { useToast } from 'primevue/usetoast';

import {
    Button, Message, Tabs, Tab, TabList, TabPanel, TabPanels,
    ProgressSpinner, Skeleton,
} from 'primevue';

const toast = useToast();

const props = defineProps({
    global: {
        type: Object,
        required: true,
    },
    campaign: {
        type: Object,
        required: true,
    },
});

const is_loading = ref(false);
const campaign = ref({});
const game = ref({});
const user = ref({});
const purchases = ref([]);
const vouchers = ref([]);
const drawlots = ref([]);
const gifts = ref([]);
const maxGiftsQty = ref(0);
const hasError = ref(null);

const getAPI = (params = {}) => {
    try {
        return route('hc.api.campaigns.test-api', {
            ...params,
            campaignId: props.campaign.campaign_id,
        });
    } catch (error) {
        console.error(error?.message);
        throw error;
    } finally {
    }
};

const updateData = (data) => {
    if (data?.campaign) {
        campaign.value = data.campaign;
    }
    if (data?.game) {
        game.value = data.game;
    }
    if (data?.user) {
        user.value = data.user;
    }
    if (data?.purchases) {
        purchases.value = data.purchases;
    }
    if (data?.vouchers) {
        vouchers.value = data.vouchers;
    }
    if (data?.drawlots) {
        drawlots.value = data.drawlots;
    }
    if (data?.gifts) {
        gifts.value = data.gifts;
    }
    if (data?.max_gifts_qty) {
        maxGiftsQty.value = data.max_gifts_qty;
    }
};

const fetch = async () => {

    is_loading.value = true;
    try {
        const api = getAPI();
        const response = await ApiClient.get(api);
        updateData(response.data);
    } catch (error) {
        hasError.value = error?.message ?? null;
        console.error(error?.message);
    } finally {
        is_loading.value = false;
    }
};

const setPurchase = async (e, purchase, status) => {
    if (!purchase?.id) {
        return;
    }
    is_loading.value = true;
    try {
        const api = getAPI({
            act: 'set-purchase',
            purchase_id: purchase.id,
            status: status,
        });
        const response = await ApiClient.post(api);
        updateData(response.data);
        if (response.data?.action_result) {
            toast.add({
                group: 'bc',
                severity: 'success',
                summary: '設定完成',
                detail: `訂單 ${purchase.id} 已設為已付款狀態`,
            });
        }
    } catch (error) {
        toast.add({
            group: 'bc',
            severity: 'error',
            summary: '設定失敗',
            detail: `訂單 ${purchase.id} 操作失敗: ${error?.message}`,
        });
    } finally {
        is_loading.value = false;
    }
};

const lotteryDrop = async (e, lottery) => {
    if (!lottery?.pid) {
        return;
    }

    is_loading.value = true;

    try {
        const api = getAPI({
            act: 'lottery-drop',
            lottery_id: lottery.pid,
        });
        const response = await ApiClient.post(api);
        updateData(response.data);
    } catch (error) {
        toast.add({
            group: 'bc',
            severity: 'error',
            summary: '設定失敗',
            detail: `序號 ${lottery.pid} 操作失敗: ${error?.message}`,
        });
    } finally {
        is_loading.value = false;
    }
};

const giftDrop = async (e, gift) => {
    if (!gift?.id) {
        return;
    }
    is_loading.value = true;
    try {
        const api = getAPI({
            act: 'gift-drop',
            gift_id: gift.id,
            purchase_id: gift.order_id,
            gift_product_id: gift.product_id,
        });
        const response = await ApiClient.post(api);
        updateData(response.data);
    } catch (error) {
        toast.add({
            group: 'bc',
            severity: 'error',
            summary: '回收失敗',
            detail: `贈品 ${gift.id} 操作失敗: ${error?.message}`,
        });
    } finally {
        is_loading.value = false;
    }
};

const voucherDrop = async (e, voucher) => {
    if (!voucher?.id) {
        return;
    }
    is_loading.value = true;
    try {
        const api = getAPI({
            act: 'voucher-drop',
            voucher_id: voucher.id,
        });
        const response = await ApiClient.post(api);
        updateData(response.data);
    } catch (error) {
        toast.add({
            group: 'bc',
            severity: 'error',
            summary: '回收失敗',
            detail: `優惠券 ${voucher.id} 操作失敗: ${error?.message}`,
        });
    } finally {
        is_loading.value = false;
    }
};

const getPurchaseStatus = (status) => {
    const mapping = {
        '0': '等待付款',
        '4': '已付款',
        '8': '出貨中',
        '9': '已出貨',
    };
    return mapping?.[status] || status;
};

const getDrawlotCount = () => {
    const total = drawlots.value.length;
    const drawed = drawlots.value.filter(lot => lot.drawed).length;
    return `${drawed} / ${total}`;
};

const getDate = (d) => {
    if (d) {
        return new Date(d).toLocaleString('zh-TW');
    }
    return '--';
};

onMounted(async () => {
    await fetch();
});
</script>
